// Product Activity (CONTROL V4 "Product activity", handoff §7g): the shapes the
// API returns and the design's own wording and tones. Every figure is the
// API's effective value (cycle-effective.js); this screen filters, formats and
// explains, it never recomputes a credited weight.

import { DAYS, MONS, parseIsoLocal } from '../timesheets/time'
import { itemExport, itemName } from './itemName'

// Whose point stands in for a scan or cycle with none of its own (product-activity.js gps_from).
export type GpsFrom = 'pick' | 'receipt' | 'scanner'
export const GPS_FROM_LABEL: Record<GpsFrom, string> = { pick: 'the pick', receipt: 'the receipt', scanner: 'the receipter’s nearest scan' }

export type ServerDayState = 'LIVE' | 'CLOSED' | 'LOCKED'
export type DayTone = 'live' | 'closed' | 'locked' | 'future'
export type Flag = 'unassigned' | 'unmatched-qc' | 'qc' | 'adjusted' | 'late' | 'inferred' | 'excluded' | 'separate'

export interface Cycle {
  id: string
  item_code: string
  // Keyed on the phone's record id: a Tagless handover (itemName.tsx).
  tagless?: boolean
  work_date: string
  eff_time: string | null
  // The winning PREASSIGN's and the selected PICK's times, on the site's
  // clock like eff_time; and the team the worker was clocked on to when the
  // cycle was captured (h 2649-2653, 2761-2766).
  pre_time?: string | null
  pick_time?: string | null
  team_id?: string | null
  team_name?: string | null
  // The pick's recorded GPS point, for the Map view (h 2701-2703).
  gps?: { lat: number; lng: number } | null
  // The PICK's, or the receipt's where the PICK recorded none.
  gps_from?: GpsFrom | null
  device_captured_at: string
  server_received_at: string
  human_id: string | null
  worker_name: string | null
  site_id: string
  site_name: string | null
  location: string
  location_ids: { level1_id: string | null; level2_id: string | null; level3_id: string | null }
  picking_job_id: string | null
  picking_job_name: string | null
  product: string | null
  batch_id: string | null
  batch_code: string | null
  gross_kg: number | null
  qc_factor: number | null
  credited_kg: number | null
  // The Site's estimated item weight, only while the cycle has no weight
  // (h 2064-2068). A placeholder: never a gross, a credit or a total.
  estimated_kg?: number | null
  // The client never weighs: gross_kg is the Site's estimate and counts.
  weight_estimated?: boolean
  quality_name: string | null
  // 28g: a tied scan's deduction not yet in credited_kg (applied at close).
  qc_provisional?: { quality_name: string | null; qc_factor: number | null } | null
  status: 'included' | 'excluded'
  display_status: 'accepted' | 'inferred' | 'excluded'
  flags: Flag[]
  // h 2714-2725: a cycle made by hand from a not-selected claim names the
  // cycle it came from; deleting it (its only undo) leaves it readable here.
  recognised_from_cycle_id?: string | null
  deleted?: boolean
  // §7g.1: PRE_HISTORY_ID, PICK_HISTORY_ID and HISTORY_ID (the time the
  // cycle is dated by), as instants and as the legacy's .NET ticks (h 2608,
  // text: they exceed a JS number). The screen never prints these names
  // (h 2745-2749).
  timing?: {
    pre_history_id: string | null
    pick_history_id: string | null
    history_id: string | null
    pre_history_ticks?: string | null
    pick_history_ticks?: string | null
    history_ticks?: string | null
  }
  captured: {
    human_id: string | null
    gross_kg: number | null
    qc_factor: number | null
    status: string
    picking_job_id: string | null
    worker_name?: string | null
  }
}

export interface Option {
  id: string
  name: string
}

export interface LocationOption {
  id: string
  site_id: string
  site_name: string
  level: number
  label: string
}

export interface ListResponse {
  cycles: Cycle[]
  truncated: boolean
  day_states: Record<string, ServerDayState>
  workers: Option[]
  jobs: Option[]
  locations: LocationOption[]
}

export interface QualityScan {
  id: string
  device_event_id: string
  quality_name: string | null
  qc_factor: number | null
  matched: boolean
  match_reason: string | null
  server_received_at: string | null
  created_at: string
  attached: boolean
  // 28g (h 2627-2636): what the association decided, under the rule wording
  // stored with it, and whether the deduction is still waiting for the close.
  outcome?: 'attached' | 'no_receipt' | 'ambiguous' | 'several_on_cycle' | 'late' | 'held_for_review' | null
  provisional?: boolean
  effective_at?: string | null
  effective_local_time?: string | null
  rule_text?: string | null
  captured_at?: string | null
  local_time?: string | null
  // D7.3/D7.6: a scan whose quality set did not check out is held for review (applied nothing) until a person accepts it.
  review_hold?: string | null
  review_accepted_at?: string | null
  set_source?: 'device' | 'team_derived' | 'control_override' | null
  // D7.4 C: the quality asked for a photo when the scan was matched to it. Written once; a later edit never re-judges it.
  photo_required_at_capture?: boolean | null
  // The quality has since been archived (the scan keeps its own label and factor).
  quality_archived?: boolean
  // A scan this cycle was one of several candidates for, not tied to it.
  candidate_only?: boolean
  // The point the scanning device recorded (evidence only, never a location).
  gps?: { lat: number; lng: number } | null
  // Set when the phone recorded no point on the scan: the point shown is the
  // item's, the PICK's or else the receipt's.
  gps_from?: GpsFrom | null
  // The photos the phone has sent for this scan so far, by index. They follow the scan, sometimes much later.
  photos?: number[]
}

export interface Adjustment {
  id: string
  kind: 'change' | 'revert'
  field: string
  field_label: string
  what: string
  why: string
  who: string
  at: string
  undone: boolean
  blocked_by_later: boolean
  can_undo: boolean
  by_merge?: boolean
  // A live bin weight share (§7k.2b): the bin that set it, shown where Undo
  // would sit, since only a re-weigh of the bin changes it.
  set_by_bin?: string | null
  set_by_batch_id?: string | null
  // h 2795: an entry with no undo here says why, and a separate pick points
  // at the other cycle it made or came from.
  no_undo?: string | null
  other_cycle?: { id: string; label: string; deleted: boolean } | null
  // h 2521: the bulk edit this change came from, undone as one action.
  bulk?: { id: string; what: string; cycles: number; undone: boolean } | null
}

// A bulk change before it is applied (POST /bulk/preview): how many of the
// selection it would change, and each one it would leave alone and why.
export interface BulkBlockedRow {
  id: string
  item_code: string | null
  tagless?: boolean
  work_date: string | null
  reason: string
  text: string
}
export interface BulkPreview {
  selected: number
  changing: string[]
  what: string
  blocked: BulkBlockedRow[]
}

// One bulk edit (GET /bulk/:id), with what undoing it would do now; `undo`
// is null once it has been undone.
export interface BulkEdit {
  id: string
  what: string
  why: string
  who: string
  at: string
  selected_count: number
  changed_count: number
  undone: boolean
  undo: { reverting: number; kept_text: string[]; locked_dates: string[]; can_undo: boolean } | null
}

// One "Why this cycle" line as the server words it (routes/product-activity.js
// ruleLinesOf): the Processing rules worked example prints the same shape.
export interface RuleLine {
  key: string
  ok: boolean
  text: string
  rule: string
  at: string | null
}

export interface Detail {
  cycle: Cycle
  day_state: ServerDayState
  raw_claim: {
    id: string
    producer_id: string
    device_event_id: string
    claim_type: string | null
    self_scan?: boolean
    // Who scanned the receipt: the named operator, else the device.
    receipter?: string | null
    device_captured_at: string
    server_received_at: string
    gps: { lat: number; lng: number } | null
    // The photos a Crew phone has sent for this claim so far, by index.
    photos?: number[]
  } | null
  quality_scans: QualityScan[]
  decision_history: { rule: string; timestamp?: string; detail?: string; actor?: string; text?: string; rule_text?: string; at?: string; ok?: boolean }[]
  // Every claim the engine read into this cycle, and the role it gave it.
  claims?: CycleClaim[]
  // The server's "Why this cycle" lines, each keyed to the rule that fired,
  // in event order.
  rule_lines?: RuleLine[]
  adjustments: Adjustment[]
  // The bin's own lifecycle (h 4544-4567), from the batch record; stamps are
  // "YYYY-MM-DD HH:MM" on the site's clock, null where the step never happened.
  batch?: CycleBatch | null
}

export interface CycleBatch {
  id: string
  batch_code: string
  bin_code: string | null
  completed_local: string | null
  completed_by: string | null
  pick_temp_c: number | null
  receipted_local: string | null
  receipted_by: string | null
  packhouse_temp_c: number | null
  weighed_kg: number | null
  weighed_local: string | null
  weighed_by: string | null
  weighed_source: string | null
}

// One event with a device GPS point, for the drawer's Evidence map (h 2709,
// V4 paEvPoints). `off` is an event the engine did not select, drawn hollow;
// `link` marks the winning claim and its receipt, joined by a dashed line.
export interface EvidencePoint {
  key: string
  lat: number
  lng: number
  t: string
  kind: string
  who: string
  state: string
  tone: string
  off: boolean
  link?: 'claim' | 'receipt'
}

// "3 Sep" over "10:40": a batch stamp split for the stacked time column.
export function stampParts(local: string | null): { day: string; clock: string } {
  const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/.exec(local || '')
  if (!m) return { day: '', clock: '—' }
  const d = parseIsoLocal(m[1])
  return { day: d.getDate() + ' ' + MON3[d.getMonth()], clock: m[2] }
}

export interface CycleClaim {
  id: string
  role: 'pick_selected' | 'pick_superseded' | 'preassign_won' | 'preassign_lost' | 'staged' | 'separated'
  rule: string
  claim_type: string
  // A picker's own scan in Crew (Selfscan Harvest), stored as a PICK.
  self_scan?: boolean
  // A receiver typed the item's weight in Crew (Add weight to bin): a weighed PICK, as a Field scale sends.
  crew_weigh?: boolean
  producer_id: string
  device_event_id?: string
  // The photos the phone has sent for this claim so far, by index.
  photos?: number[]
  captured_at: string
  received_at: string
  worker_name: string | null
  gross_kg: number | null
  // A receipt's scanner (never the picker it credits); null on a PREASSIGN.
  receipter?: string | null
  // A claim recognised as a separate pick: the cycle it now is.
  made_cycle_id?: string | null
  // The point the device recorded on this claim (h 2709; evidence only).
  gps?: { lat: number; lng: number } | null
}

// The day-state tones, shared with Timesheets (V4 TONE).
export const TONE: Record<DayTone, { bg: string; line: string; ink: string; dot: string }> = {
  live: { bg: '#fbeecd', line: '#eedcae', ink: '#7a5410', dot: '#c78f2c' },
  closed: { bg: '#f1f4e8', line: '#d3ddba', ink: '#3f4a2c', dot: '#7a8a5e' },
  locked: { bg: '#f6f0e6', line: '#e2dacb', ink: '#5c5449', dot: '#857c6d' },
  future: { bg: '#f6f0e6', line: '#e2dacb', ink: '#857c6d', dot: '#c1b7a4' },
}

// V4 PA_STATE_TEXT.
export const STATE_TEXT: Record<DayTone, { name: string; line: string }> = {
  live: { name: 'Live', line: 'Data is still arriving and the engine may reinterpret cycles. Edits made now are provisional.' },
  closed: { name: 'Closed', line: 'The operational day is complete. New events arrive as late data for approval, not silent rewrites.' },
  locked: { name: 'Locked', line: 'Payroll is finalised. Changes need a deliberate administrative unlock.' },
  future: { name: 'Not yet', line: 'This day has not happened, so there is nothing to interpret yet.' },
}

// V4 CHIP: the row notes, in the order the design prints them.
export const CHIP: Partial<Record<Flag, { label: string; bg: string; fg: string }>> = {
  qc: { label: 'QC', bg: '#fbeecd', fg: '#7a5410' },
  'unmatched-qc': { label: 'QC unmatched', bg: '#f8ddcd', fg: '#6e3312' },
  adjusted: { label: 'Adjusted', bg: '#f6f0e6', fg: '#5c5449' },
  late: { label: 'Late data', bg: '#fdf1e8', fg: '#8c491a' },
  unassigned: { label: 'No worker', bg: '#f8ddcd', fg: '#6e3312' },
  inferred: { label: 'Incomplete', bg: '#fbeecd', fg: '#7a5410' },
  excluded: { label: 'Excluded', bg: '#f6f0e6', fg: '#857c6d' },
  separate: { label: 'Manual cycle', bg: '#f1f4e8', fg: '#3f4a2c' },
}
export const CHIP_ORDER: Flag[] = ['qc', 'unmatched-qc', 'adjusted', 'late', 'unassigned', 'inferred', 'excluded', 'separate']

// The filter chips above the table (V4 paFlagChips).
export const FLAG_FILTERS: { key: Flag; label: string }[] = [
  { key: 'unassigned', label: 'Unassigned worker' },
  { key: 'unmatched-qc', label: 'Unmatched QC' },
  { key: 'qc', label: 'QC applied' },
  { key: 'adjusted', label: 'Adjusted' },
  { key: 'late', label: 'Late-data affected' },
]

// The filters behind the Filters button (h 2649-2653, 2761-2766; the drawer
// rule, h 321-347): a time-of-day window, a weight range, a quality, a
// location and a team. Empty strings are "any".
export type TimeBasis = 'eff' | 'pre' | 'pick'
export const TIME_BASES: { key: TimeBasis; label: string }[] = [
  { key: 'eff', label: 'Effective time' },
  { key: 'pre', label: 'Assign time' },
  { key: 'pick', label: 'Pick time' },
]
export const NO_QUALITY = '__none__'
export const NOT_CLOCKED = '__none__'
export interface MoreFilters {
  timeBy: TimeBasis
  timeFrom: string
  timeTo: string
  minKg: string
  maxKg: string
  quality: string
  // 'site:<id>' for a whole site, else a structure location id.
  location: string
  team: string
}
export const NO_MORE: MoreFilters = { timeBy: 'eff', timeFrom: '', timeTo: '', minKg: '', maxKg: '', quality: '', location: '', team: '' }

const kgBound = (s: string): number | null => {
  if (!s.trim()) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}
const timeOf = (c: Cycle, by: TimeBasis) => (by === 'pre' ? c.pre_time : by === 'pick' ? c.pick_time : c.eff_time) || null

// Both ends of the window are inclusive; a window whose start is after its end
// runs over midnight (22:00-02:00). The window is a time of day, applied to
// every day in the period, so on a one-day period it is the finer-than-a-day
// filter. A cycle with no time of that kind (no pick yet) is outside any
// window, and one with no weight is outside any weight range.
export function matchesMore(c: Cycle, m: MoreFilters): boolean {
  if (m.timeFrom || m.timeTo) {
    const t = timeOf(c, m.timeBy)
    if (!t) return false
    if (m.timeFrom && m.timeTo && m.timeFrom > m.timeTo) {
      if (t < m.timeFrom && t > m.timeTo) return false
    } else {
      if (m.timeFrom && t < m.timeFrom) return false
      if (m.timeTo && t > m.timeTo) return false
    }
  }
  const lo = kgBound(m.minKg)
  const hi = kgBound(m.maxKg)
  if (lo !== null || hi !== null) {
    if (c.gross_kg === null) return false
    if (lo !== null && c.gross_kg < lo) return false
    if (hi !== null && c.gross_kg > hi) return false
  }
  if (m.quality && (m.quality === NO_QUALITY ? !!c.quality_name : c.quality_name !== m.quality)) return false
  if (m.location) {
    if (m.location.startsWith('site:')) {
      if (c.site_id !== m.location.slice(5)) return false
    } else {
      // A block holds its tunnels and rows, so any level may match.
      const ids = c.location_ids || { level1_id: null, level2_id: null, level3_id: null }
      if (![ids.level1_id, ids.level2_id, ids.level3_id].includes(m.location)) return false
    }
  }
  if (m.team && (m.team === NOT_CLOCKED ? !!c.team_id : c.team_id !== m.team)) return false
  return true
}

// One per filter in use, for the badge on the button.
export function moreCount(m: MoreFilters): number {
  return (
    (m.timeFrom || m.timeTo ? 1 : 0) +
    (kgBound(m.minKg) !== null || kgBound(m.maxKg) !== null ? 1 : 0) +
    (m.quality ? 1 : 0) +
    (m.location ? 1 : 0) +
    (m.team ? 1 : 0)
  )
}

// What the drawer is filtering by, said beside the button (h 321-347).
export function moreSummary(m: MoreFilters, names: { location: (id: string) => string; team: (id: string) => string }): string[] {
  const out: string[] = []
  if (m.timeFrom || m.timeTo) {
    const by = TIME_BASES.find((b) => b.key === m.timeBy)!.label
    out.push(m.timeFrom && m.timeTo ? `${by} ${m.timeFrom}–${m.timeTo}` : m.timeFrom ? `${by} from ${m.timeFrom}` : `${by} until ${m.timeTo}`)
  }
  const lo = kgBound(m.minKg)
  const hi = kgBound(m.maxKg)
  if (lo !== null && hi !== null) out.push(`Gross ${lo}–${hi} kg`)
  else if (lo !== null) out.push(`Gross at least ${lo} kg`)
  else if (hi !== null) out.push(`Gross at most ${hi} kg`)
  if (m.quality) out.push(m.quality === NO_QUALITY ? 'No quality' : `Quality ${m.quality}`)
  if (m.location) out.push(names.location(m.location))
  if (m.team) out.push(m.team === NOT_CLOCKED ? 'Not clocked on' : `Team ${names.team(m.team)}`)
  return out
}

// The Map view (h 2701-2703) places a cycle at its pick's recorded GPS point.
// Nothing else is a position: a cycle without one is counted, never guessed.
// The marker colour is the cycle's status, fixed because it sits on the tiles.
export const MAP_STATUS: Record<Cycle['display_status'], { label: string; fill: string }> = {
  accepted: { label: 'Accepted', fill: '#2f8a4e' },
  inferred: { label: 'Inferred', fill: '#d99a1e' },
  excluded: { label: 'Excluded', fill: '#8a8f98' },
}
export function mapSplit(rows: Cycle[]): { placed: (Cycle & { gps: { lat: number; lng: number } })[]; unplaced: number } {
  const placed = rows.filter((c): c is Cycle & { gps: { lat: number; lng: number } } => !!c.gps)
  return { placed, unplaced: rows.length - placed.length }
}
export function mapTooltip(c: Cycle): string {
  const w = c.gross_kg === null ? (c.estimated_kg ? estKg(c.estimated_kg) : 'not weighed') : kg(c.gross_kg)
  return `${itemName(c)} · ${c.worker_name || 'Unassigned'} · ${w}`
}

// V4 paNeedsAttention: the four states someone has to do something about.
export function needsAttention(c: Cycle): boolean {
  return c.flags.includes('unmatched-qc') || c.flags.includes('unassigned') || c.display_status === 'inferred' || c.flags.includes('late')
}

const DOW3 = DAYS.map((d) => d.slice(0, 3))
const MON3 = MONS.map((m) => m.slice(0, 3))

// "Mon 28 Sep" and "Mon 28", as the design prints a day.
export function dayLabel(iso: string): string {
  const d = parseIsoLocal(iso)
  return DOW3[d.getDay()] + ' ' + d.getDate() + ' ' + MON3[d.getMonth()]
}
export function dayShort(iso: string): string {
  const d = parseIsoLocal(iso)
  return DOW3[d.getDay()] + ' ' + d.getDate()
}

export function kg(n: number | null | undefined): string {
  return n === null || n === undefined || Number.isNaN(n) ? '—' : n.toFixed(1) + ' kg'
}

// An estimate reads as one wherever it is shown, to the gram the Site set
// ("est. 0.18 kg"), so it cannot be mistaken for a weighed kg figure.
export function estKg(n: number): string {
  return 'est. ' + Number(n.toFixed(3)) + ' kg'
}
export const WEIGHT_EST_TITLE = "Estimated: not weighed, so the site's item weight is the weight. It counts, credits and pays like a weighed one until a weight is recorded."
export const EST_TITLE = "Not weighed. This is the site's estimated item weight: a placeholder that is never paid, credited or invoiced, and is replaced when a weight is recorded."

// The rows shown with no weight yet, and what their estimates add up to. Kept
// apart from the gross and credited totals, which count only weights.
export function unweighedSummary(rows: Cycle[]): string | null {
  const open = rows.filter((c) => c.gross_kg === null && c.status === 'included')
  if (!open.length) return null
  // A Site with no estimate set gives its cycles none; the sum says so.
  const withEst = open.filter((c) => (c.estimated_kg || 0) > 0)
  const est = withEst.reduce((a, c) => a + (c.estimated_kg || 0), 0)
  const n = `${open.length} not weighed`
  if (!withEst.length) return n
  return `${n} (${estKg(est)}${withEst.length < open.length ? ` for ${withEst.length}` : ''}, not in these totals)`
}

export function pct(f: number): string {
  return Math.round(f * 100) + '%'
}

// A wall-clock "HH:MM" in the viewer's browser, for events that carry only a
// timestamp. The cycle's own time (eff_time) is the site's clock, from the API.
export function clock(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
}

export function toneOf(date: string, today: string, states: Record<string, ServerDayState>): DayTone {
  if (date > today) return 'future'
  return (states[date] || 'LIVE').toLowerCase() as DayTone
}

// Worst first: a locked day forbids most, a live day warns most (V4 banner).
const STRICT: DayTone[] = ['locked', 'live', 'closed', 'future']
export function strictest(tones: DayTone[]): DayTone {
  for (const t of STRICT) if (tones.includes(t)) return t
  return 'live'
}

// V4 paMonth: dots count real cycles per day.
export function activityLevel(n: number): 0 | 1 | 2 | 3 {
  if (!n) return 0
  return n >= 6 ? 3 : n >= 3 ? 2 : 1
}
export const DOT: Record<1 | 2 | 3, string> = { 1: '#c3d0a5', 2: '#8f9e6f', 3: '#5f6d45' }

// The rows of product-activity.xlsx (h 4519-4525): one line per cycle in view,
// effective values, the flags as the chips name them. Built from the rows on
// screen, not re-queried. Weights stay numbers so the sheet can sum them; an
// unweighed cycle's gross is blank, never its estimate.
export const EXPORT_HEAD = ['Date', 'Time', 'Item', 'Worker', 'Site', 'Location', 'Picking job', 'Product', 'Batch', 'Gross kg', 'QC factor', 'Credited kg', 'Quality', 'Status', 'Notes']
export function exportRows(rows: Cycle[]): (string | number | null)[][] {
  const dp2 = (v: number | null) => (v === null ? null : Math.round(v * 100) / 100)
  return [
    EXPORT_HEAD,
    ...rows.map((c) => [
      c.work_date,
      c.eff_time || '',
      itemExport(c),
      c.worker_name || 'Unassigned',
      c.site_name || '',
      c.location,
      c.picking_job_name || '',
      c.product || '',
      c.batch_code || '',
      dp2(c.gross_kg),
      c.qc_factor,
      dp2(c.credited_kg),
      c.quality_name || '',
      c.status,
      CHIP_ORDER.filter((f) => c.flags.includes(f)).map((f) => CHIP[f]!.label).join('; '),
    ]),
  ]
}
